// src/components/McpConnections/ToolItem.js
import { useState } from 'react';
import { callTool } from './api';
import Spinner from './Spinner';
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { DataState } from '../../context/DataProvider';

// The isExpanded and onToggle props are new. They are controlled by the parent.
const ToolItem = ({ tool, connectionId, isExpanded, onToggle }) => {
  const {userGoogleId:accessToken} = DataState();
  const [formData, setFormData] = useState({});
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleCallTool = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setResult(null);

    const args = {};
    for (const key in formData) {
      const prop = tool.inputSchema?.properties?.[key];
      let value = formData[key];
      if (value !== '' && value !== undefined) {
          if (prop?.type === 'number') args[key] = parseFloat(value);
          else if (prop?.type === 'boolean') args[key] = Boolean(value);
          else args[key] = value;
      }
    }

    try {
      const response = await callTool(accessToken, connectionId, tool.name, args);
      setResult({ data: response, isError: false });
    } catch (error) {
      const errorData = error.response?.detail || error.message;
      setResult({ data: errorData, isError: true });
    } finally {
      setIsLoading(false);
    }
  };

  const schema = tool.inputSchema || { properties: {} };
  const properties = schema.properties || {};

  return (
    <li className="bg-zinc-800 border border-zinc-700 rounded-lg overflow-hidden">
        {/* Make the header clickable to toggle expansion */}
        <div 
            className="p-4 flex justify-between items-center cursor-pointer hover:bg-zinc-700/50"
            onClick={onToggle}
        >
            <div>
                <p className="font-semibold text-gray-200">{tool.name}</p>
                <p className="text-sm text-gray-400">{tool.description}</p>
            </div>
            <span className="text-gray-400 text-lg">
                {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
            </span>
        </div>

        {/* The form and result are now rendered conditionally */}
        {isExpanded && (
            <div className="p-4 border-t border-zinc-700">
                <form onSubmit={handleCallTool}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.keys(properties).map(key => {
                        // ... (the form field rendering logic is the same as before) ...
                        const prop = properties[key];
                        const isRequired = schema.required?.includes(key);
                        const inputType = prop.type === 'number' ? 'number' : (prop.type === 'boolean' ? 'checkbox' : 'text');
                        
                        return (
                          <div key={key}>
                            <label className="text-xs font-medium text-gray-400">
                              {key} {isRequired && <span className="text-red-500">*</span>}
                              <span className="ml-2 opacity-70">({prop.type})</span>
                            </label>
                            <input
                              type={inputType}
                              name={key}
                              checked={formData[key] || false}
                              onChange={handleInputChange}
                              placeholder={prop.description || ''}
                              className="mt-1 w-full bg-zinc-900 border border-zinc-600 rounded-md px-3 py-2 text-white placeholder-zinc-500 focus:ring-2 focus:ring-[#5652CF] focus:outline-none"
                            />
                          </div>
                        );
                    })}
                    </div>
                    <div className="mt-4 flex items-center gap-4">
                    <button
                        type="submit"
                        disabled={isLoading}
                        className="px-4 py-2 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 disabled:bg-zinc-600 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {isLoading && <Spinner size="1.2rem" />}
                        Call Tool
                    </button>
                    </div>
                </form>
                {result && (
                    <div className="mt-4">
                        <h4 className="text-sm font-semibold text-gray-300 mb-2">Result:</h4>
                        <pre className={`p-4 rounded-md text-xs max-h-60 overflow-y-auto ${result.isError ? 'bg-red-900/50 text-red-300' : 'bg-green-900/50 text-green-300'}`}>
                            {JSON.stringify(result.data, null, 2)}
                        </pre>
                    </div>
                )}
            </div>
        )}
    </li>
  );
};

export default ToolItem;